// ============================================================================
// printReport — standard report printing utility (introduced July 2026)
// ============================================================================
// Builds a standalone, management-friendly HTML document (header block, KPI
// summary, data table, optional secondary sections) inside a hidden iframe and
// invokes the browser's print dialog. Using an iframe (not window.open) avoids
// popup blockers and leaves the app's DOM/styles untouched.
//
// This is the ONE print pattern for report screens — new reports should call
// this rather than inventing their own window.print flows.

export interface PrintKpi {
  label: string;
  value: string;
}

export interface PrintColumn {
  header: string;
  align?: 'left' | 'right' | 'center';
}

export interface PrintSection {
  title: string;
  columns: PrintColumn[];
  rows: string[][];
}

export interface PrintReportOptions {
  /** Report name, e.g. "Occupancy Chart". */
  title: string;
  /** Property / hotel name shown above the title. */
  propertyName?: string;
  /** e.g. "25 Jul 2026" or "01 Jul 2026 – 25 Jul 2026". */
  periodLabel?: string;
  /** KPI tiles rendered under the header. */
  kpis?: PrintKpi[];
  /** Main data table. */
  columns: PrintColumn[];
  rows: string[][];
  /** Optional secondary tables (e.g. food-plan summary). */
  sections?: PrintSection[];
  /** Small note under the table (e.g. "ID numbers masked"). */
  footerNote?: string;
  /** Landscape orientation for wide tables (default true). */
  landscape?: boolean;
}

const esc = (s: string): string =>
  s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

const renderTable = (columns: PrintColumn[], rows: string[][]): string => `
  <table>
    <thead>
      <tr>${columns.map((c) => `<th style="text-align:${c.align ?? 'left'}">${esc(c.header)}</th>`).join('')}</tr>
    </thead>
    <tbody>
      ${rows.map((r) => `<tr>${r.map((cell, i) =>
        `<td style="text-align:${columns[i]?.align ?? 'left'}">${esc(cell)}</td>`).join('')}</tr>`).join('')}
    </tbody>
  </table>`;

export const printReport = (opts: PrintReportOptions): void => {
  const {
    title, propertyName, periodLabel, kpis = [],
    columns, rows, sections = [], footerNote, landscape = true,
  } = opts;

  const generatedAt = new Date().toLocaleString('en-IN', {
    day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
  });

  const html = `<!DOCTYPE html>
<html>
<head>
<meta charset="utf-8" />
<title>${esc(title)}</title>
<style>
  @page { size: A4 ${landscape ? 'landscape' : 'portrait'}; margin: 12mm; }
  * { box-sizing: border-box; margin: 0; padding: 0; }
  body {
    font-family: 'Segoe UI', Arial, sans-serif;
    color: #1e293b;
    font-size: 10px;
    padding: 4px;
  }
  .report-header {
    display: flex; justify-content: space-between; align-items: flex-end;
    border-bottom: 2px solid #1e3a5f; padding-bottom: 8px; margin-bottom: 10px;
  }
  .report-header .property { font-size: 14px; font-weight: 700; color: #1e3a5f; }
  .report-header .title    { font-size: 12px; font-weight: 600; margin-top: 2px; }
  .report-header .period   { font-size: 10px; color: #475569; margin-top: 2px; }
  .report-header .meta     { font-size: 8.5px; color: #64748b; text-align: right; }
  .kpi-row {
    display: flex; flex-wrap: wrap; gap: 6px; margin-bottom: 10px;
  }
  .kpi {
    border: 1px solid #cbd5e1; border-radius: 4px;
    padding: 4px 10px; min-width: 82px; background: #f8fafc;
    -webkit-print-color-adjust: exact; print-color-adjust: exact;
  }
  .kpi .label { font-size: 8px; color: #64748b; text-transform: uppercase; letter-spacing: 0.03em; }
  .kpi .value { font-size: 12px; font-weight: 700; color: #0f172a; margin-top: 1px; }
  table { width: 100%; border-collapse: collapse; margin-bottom: 10px; }
  th {
    background: #e2e8f0; border: 1px solid #94a3b8;
    padding: 3px 5px; font-size: 9px; font-weight: 600;
    -webkit-print-color-adjust: exact; print-color-adjust: exact;
  }
  td { border: 1px solid #cbd5e1; padding: 3px 5px; font-size: 9px; }
  tbody tr:nth-child(even) td { background: #f8fafc; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
  thead { display: table-header-group; }  /* repeat header on every page */
  tr { page-break-inside: avoid; }
  .section-title { font-size: 10px; font-weight: 600; margin: 8px 0 4px; color: #1e3a5f; }
  .section table { width: auto; min-width: 40%; }
  .footer-note { font-size: 8.5px; color: #64748b; margin-top: 6px; }
</style>
</head>
<body>
  <div class="report-header">
    <div>
      ${propertyName ? `<div class="property">${esc(propertyName)}</div>` : ''}
      <div class="title">${esc(title)}</div>
      ${periodLabel ? `<div class="period">${esc(periodLabel)}</div>` : ''}
    </div>
    <div class="meta">Generated ${esc(generatedAt)}<br/>CRS</div>
  </div>
  ${kpis.length ? `<div class="kpi-row">${kpis.map((k) =>
    `<div class="kpi"><div class="label">${esc(k.label)}</div><div class="value">${esc(k.value)}</div></div>`).join('')}</div>` : ''}
  ${renderTable(columns, rows)}
  ${sections.map((s) => `<div class="section"><div class="section-title">${esc(s.title)}</div>${renderTable(s.columns, s.rows)}</div>`).join('')}
  ${footerNote ? `<div class="footer-note">${esc(footerNote)}</div>` : ''}
</body>
</html>`;

  const iframe = document.createElement('iframe');
  iframe.style.position = 'fixed';
  iframe.style.right = '0';
  iframe.style.bottom = '0';
  iframe.style.width = '0';
  iframe.style.height = '0';
  iframe.style.border = '0';
  document.body.appendChild(iframe);

  const doc = iframe.contentWindow?.document;
  if (!doc) {
    iframe.remove();
    return;
  }
  doc.open();
  doc.write(html);
  doc.close();

  // Give the iframe a tick to lay out before printing; clean up afterwards.
  // afterprint doesn't fire reliably cross-browser from iframes, so fall back
  // to a generous timeout for removal.
  setTimeout(() => {
    iframe.contentWindow?.focus();
    iframe.contentWindow?.print();
    // remove() is a no-op when the node is already gone; removeChild would throw.
    setTimeout(() => { iframe.remove(); }, 60000);
  }, 150);
};

export default printReport;
